// Registration Service
// This service handles all registration logic, API calls, and business rules
// Separated from UI components for better maintainability and testing

import { apiService, type ApiResponse } from '@/services/apiService';

export interface RegistrationData {
    email: string;
    password: string;
    confirmPassword: string;
    two_factor_enabled?: boolean;
    // Note: emailCode is not used during registration
    // User will activate email first, then use OTP when logging in if 2FA is enabled
}

export interface RegistrationResponse {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
}

export interface ActivationResponse {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
}

export interface ValidationResult {
    isValid: boolean;
    message: string;
}

class RegistrationService {
    /**
     * Validates email format using regex
     */
    validateEmail(email: string): ValidationResult {
        if (!email || email.trim() === '') {
            return {
                isValid: false,
                message: 'Email is required'
            };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            return {
                isValid: false,
                message: 'Please enter a valid email address'
            };
        }

        return {
            isValid: true,
            message: 'Valid email'
        };
    }

    /**
     * Validates password strength
     */
    validatePassword(password: string): ValidationResult {
        if (!password || password === '') {
            return {
                isValid: false,
                message: 'Password is required'
            };
        }

        if (password.length < 8) {
            return {
                isValid: false,
                message: 'Password must be at least 8 characters'
            };
        }

        if (password.length > 128) {
            return {
                isValid: false,
                message: 'Password must be less than 128 characters'
            };
        }

        // Check for at least one uppercase letter
        if (!/[A-Z]/.test(password)) {
            return {
                isValid: false,
                message: 'Password must contain at least one uppercase letter'
            };
        }

        // Check for at least one lowercase letter
        if (!/[a-z]/.test(password)) {
            return {
                isValid: false,
                message: 'Password must contain at least one lowercase letter'
            };
        }

        // Check for at least one number
        if (!/\d/.test(password)) {
            return {
                isValid: false,
                message: 'Password must contain at least one number'
            };
        }

        // Check for at least one special character
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            return {
                isValid: false,
                message: 'Password must contain at least one special character'
            };
        }

        return {
            isValid: true,
            message: 'Valid password'
        };
    }

    /**
     * Validates password confirmation
     */
    validatePasswordConfirmation(password: string, confirmPassword: string): ValidationResult {
        if (!confirmPassword || confirmPassword === '') {
            return {
                isValid: false,
                message: 'Please confirm your password'
            };
        }

        if (password !== confirmPassword) {
            return {
                isValid: false,
                message: 'Passwords do not match'
            };
        }

        return {
            isValid: true,
            message: 'Passwords match'
        };
    }

    /**
     * Validates all registration fields at once
     */
    validateAllFields(registrationData: RegistrationData): ValidationResult {
        const emailValidation = this.validateEmail(registrationData.email);
        if (!emailValidation.isValid) return emailValidation;

        const passwordValidation = this.validatePassword(registrationData.password);
        if (!passwordValidation.isValid) return passwordValidation;

        const confirmPasswordValidation = this.validatePasswordConfirmation(
            registrationData.password, 
            registrationData.confirmPassword
        );
        if (!confirmPasswordValidation.isValid) return confirmPasswordValidation;

        return {
            isValid: true,
            message: 'All fields are valid'
        };
    }

    /**
     * Registers a new user
     */
    async registerUser(registrationData: RegistrationData): Promise<RegistrationResponse> {
        try {
            // Validate all fields
            const validation = this.validateAllFields(registrationData);
            if (!validation.isValid) {
                return {
                    success: false,
                    message: validation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Prepare request body
            const requestBody: any = {
                email: registrationData.email.trim(),
                password: registrationData.password,
                confirmPassword: registrationData.confirmPassword
            };

            // Add 2FA field if enabled (no emailCode needed during registration)
            if (registrationData.two_factor_enabled) {
                requestBody.two_factor_enabled = true;
            }

            // Make API call to backend
            const response = await apiService.post('/auth/register', requestBody, false); // Don't include auth for registration

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error registering user:', error);
            
            // Handle specific error cases
            if (error.statusCode === 409) {
                return {
                    success: false,
                    message: error.message || 'User already exists with this email',
                    error: 'USER_EXISTS'
                };
            }

            if (error.statusCode === 422) {
                return {
                    success: false,
                    message: error.message || 'Validation failed',
                    error: 'VALIDATION_ERROR'
                };
            }

            return {
                success: false,
                message: error.message || 'Registration failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Activates user email with activation token
     */
    async activateEmail(token: string): Promise<ActivationResponse> {
        try {
            if (!token || token.trim() === '') {
                return {
                    success: false,
                    message: 'Activation token is required',
                    error: 'VALIDATION_ERROR'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/auth/activate-email', {
                token: token.trim()
            }, false); // Don't include auth for activation

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error activating email:', error);
            
            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: error.message || 'Invalid or expired activation token',
                    error: 'INVALID_TOKEN'
                };
            }

            if (error.statusCode === 404) {
                return {
                    success: false,
                    message: error.message || 'User not found',
                    error: 'USER_NOT_FOUND'
                };
            }

            return {
                success: false,
                message: error.message || 'Email activation failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Verifies email with verification code
     */
    async verifyEmail(email: string, code: string): Promise<RegistrationResponse> {
        try {
            // Validate inputs
            const emailValidation = this.validateEmail(email);
            if (!emailValidation.isValid) {
                return {
                    success: false,
                    message: emailValidation.message,
                    error: 'INVALID_EMAIL'
                };
            }

            if (!code || code.trim().length !== 6) {
                return {
                    success: false,
                    message: 'Verification code must be 6 digits',
                    error: 'INVALID_CODE'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/auth/verify-email', {
                email: email.trim(),
                code: code.trim()
            }, false); // Don't include auth for email verification

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error verifying email:', error);
            
            // Handle specific error cases
            if (error.statusCode === 404) {
                return {
                    success: false,
                    message: error.message || 'User not found',
                    error: 'USER_NOT_FOUND'
                };
            }

            if (error.statusCode === 422) {
                return {
                    success: false,
                    message: error.message || 'Invalid or expired verification code',
                    error: 'INVALID_CODE'
                };
            }

            return {
                success: false,
                message: error.message || 'Email verification failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }
}

// Export singleton instance
export const registrationService = new RegistrationService();

// Export types for use in components
export type { RegistrationData, RegistrationResponse, ValidationResult };
